import { useContext, useEffect, useMemo, HTMLAttributes } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import Modal, { ClosableModalWrapperComponentProps } from "../modal";
import { OrganizationGroup } from "../../../model/OrganizationGroup";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
  ClearErrorAction,
  GetOrgGroupAction,
  isAddErrorAction,
  ReplaceOrgGroupAction,
  SetEntitlementsConsumedByOrgGroup,
} from "../../../actions/actionTypes";
import Feedback from "../../feedback/feedback-container";
import { FeedbackEntry } from "../../feedback";
import { Entitlement } from "../../../model/entitlement/Entitlement";
import FieldList from "../../field-list";
import GroupTypeBadge from "../../badges/group-type-badge";
import Table from "../../table";
import { TABLE_SEARCH_THRESHOLD } from "../../table/table-view";
import { ModalKeys as GroupModalKeys } from "../../pages/groups";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import FormInput from "../../form-input";
import { validityResolver } from "../../form-input/form-input-view";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NavigateAfterAction from "../../navigate-after-action";
import { OrganizationGroupLabels } from "../../../localization/organization-group";

//<editor-fold desc="Props">

export interface ViewGroupModalDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {}
export interface ViewGroupModalVisibilityProps
  extends Pick<
    ClosableModalWrapperComponentProps,
    "show" | "onClose" | "onExited"
  > {
  onShowFeedback: (feedback: FeedbackEntry | FeedbackEntry[]) => void;
}
export interface ViewGroupModalInputProps
  extends ViewGroupModalVisibilityProps {
  edit?: boolean;
  allowEdit?: boolean;
  groupId: string | undefined;
}
export interface ViewGroupModalStateProps {
  hasEntitlementChanges: boolean;

  selected: Entitlement[];
  onSetSelected: (selection: Entitlement[]) => void;
  activeSearch?: string;
  onSetActiveSearch: (s: string) => void;
}
export interface ViewGroupModalDataProps
  extends Pick<ClosableModalWrapperComponentProps, "isReady"> {
  organizationId?: string;
  group: OrganizationGroup | undefined | null;
  groupEntitlements?: Entitlement[];
  onLoadGroup: (groupId: string) => Promise<GetOrgGroupAction>;
  onUpdateGroup?: (group: OrganizationGroup) => Promise<ReplaceOrgGroupAction>;
  onLoadGroupEntitlements?: (orgId: string, groupId: string) => void;
  onSetGroupEntitlements?: (
    orgId: string,
    groupId: string,
    entitlementIds: string[]
  ) => Promise<SetEntitlementsConsumedByOrgGroup>;

  entitlements?: Entitlement[];
  onLoadEntitlements?: (orgId: string) => void;
  onClearError: (errorId: string) => ClearErrorAction;
}
export interface ViewGroupModalProps
  extends ViewGroupModalDOMProps,
    ViewGroupModalInputProps,
    ViewGroupModalStateProps,
    ViewGroupModalDataProps {}
//</editor-fold>

function ViewGroupModal(props: ViewGroupModalProps) {
  //<editor-fold desc="Local variables">
  let {
    group,
    groupId,
    onLoadGroup,
    show,
    onClose,
    edit,
    allowEdit,
    onUpdateGroup,
    onShowFeedback,
    onSetGroupEntitlements,
    groupEntitlements,
    onLoadGroupEntitlements,
    onLoadEntitlements,
    entitlements,
    organizationId,
    hasEntitlementChanges,
    selected,
    onSetSelected,
    activeSearch,
    onSetActiveSearch,
    isReady,
    onExited,
    onClearError,
  } = props;

  const { conf } = useContext(UIConfiguration);
  const groupConf =
    conf.functionality && conf.functionality.groups
      ? conf.functionality.groups
      : {};
  // this is more like a variable than a hook
  const intl = useIntl();
  const isEmployeesGroup = group ? group.type === "employees" : false;
  //</editor-fold>

  //<editor-fold desc="Hooks">
  const history = useHistory();
  const defaultValues = useMemo(
    () => ({
      name: group ? group.name : "",
      type: group ? group.type : "",
      description: group ? group.description : "",
    }),
    [group]
  );
  const { register, handleSubmit, formState, reset, watch } = useForm({
    mode: "onChange",
    defaultValues: defaultValues,
  });

  const { errors } = formState;

  const formValues = watch();

  if (show && groupEntitlements === undefined && !onLoadGroupEntitlements) {
    throw new Error(
      "ViewGroupModal: Required props missing. groupEntitlements or onLoadGroupEntitlements must be defined"
    );
  }
  if (show && entitlements === undefined && !onLoadEntitlements) {
    throw new Error(
      "ViewGroupModal: Required props missing. entitlements or onLoadEntitlements must be defined"
    );
  }
  if (show && allowEdit && (!onSetGroupEntitlements || !onUpdateGroup)) {
    throw new Error(
      "ViewGroupModal: Required props missing. onSetGroupEntitlements and onUpdateGroup must be defined when allowEdit"
    );
  }
  const groupObjId = group ? group.id : undefined;
  useEffect(() => {
    if (show && organizationId && groupObjId && onLoadGroupEntitlements) {
      onLoadGroupEntitlements(organizationId, groupObjId as string);
    }
  }, [show, organizationId, groupObjId, onLoadGroupEntitlements]);

  useEffect(() => {
    if (
      show &&
      organizationId &&
      entitlements === undefined &&
      onLoadEntitlements
    ) {
      onLoadEntitlements(organizationId);
    }
  }, [show, organizationId, entitlements, onLoadEntitlements]);
  useEffect(() => {
    if (
      show &&
      groupId &&
      (groupObjId === undefined || (groupObjId && groupObjId !== groupId)) &&
      onLoadGroup
    ) {
      onLoadGroup(groupId).then((res) => {
        if (!groupObjId && isAddErrorAction(res)) {
          // only clear error if no data exists, leave if previous data is still available and
          // let the api error notification handler show error
          onClearError(res.error?.errorId);
        }
      });
    }
  }, [show, groupId, groupObjId, onLoadGroup, onClearError]);

  useEffect(() => {
    if (defaultValues && edit) {
      reset(defaultValues);
    }
  }, [defaultValues, edit, reset]);
  //</editor-fold>
  return (
    <Modal
      onExited={onExited}
      onReloadData={() => {
        if (organizationId && (group || groupId) && onLoadGroupEntitlements) {
          onLoadGroupEntitlements(
            organizationId,
            group ? (group.id as string) : (groupId as string)
          );
        }
        if (organizationId && onLoadEntitlements) {
          onLoadEntitlements(organizationId);
        }
        if ((group || groupId) && onLoadGroup) {
          onLoadGroup(group ? (group.id as string) : (groupId as string)).then(
            (res) => {
              if (!group && isAddErrorAction(res)) {
                // only clear error if no data exists, leave if previous data is still available and
                // let the api error notification handler show error
                onClearError(res.error?.errorId);
              }
            }
          );
        }
      }}
      isReady={isReady}
      data-test-view-group-modal
      title={
        !isReady || group
          ? !edit
            ? intl.formatMessage({
                defaultMessage: "Group details",
                description: "modal heading",
              })
            : intl.formatMessage({
                defaultMessage: "Edit group details",
                description: "modal heading for edit state",
              })
          : intl.formatMessage({
              defaultMessage: "Group not found",
              description: "modal heading when group not found",
            })
      }
      show={show}
      onClose={onClose}
      backdrop={formState.isDirty || hasEntitlementChanges ? "static" : true}
      primaryButton={
        group && allowEdit
          ? !edit
            ? {
                label: intl.formatMessage({
                  defaultMessage: "Edit",
                  description: "primary button label",
                }),
              }
            : {
                label: intl.formatMessage({
                  defaultMessage: "Apply",
                  description: "primary button label for edit state",
                }),
                disabled:
                  (errors !== undefined &&
                    errors !== null &&
                    Object.keys(errors).length > 0) ||
                  !(formState.isDirty || hasEntitlementChanges),
              }
          : {
              label: intl.formatMessage({
                defaultMessage: "Close",
                description: "close button label",
              }),
            }
      }
      onPrimaryAction={
        group && allowEdit
          ? !edit
            ? () => {
                history.push(
                  "/groups/" +
                    (group ? (group.id as string) : "not_possible") +
                    "/edit"
                );
              }
            : () => {
                handleSubmit((data) => {
                  const ng: any = {
                    ...group,
                    ...data,
                  };
                  delete ng.entitlements;
                  if (onUpdateGroup && onSetGroupEntitlements) {
                    onUpdateGroup(ng as OrganizationGroup).then((res) => {
                      if (!isAddErrorAction(res)) {
                        const feedback: FeedbackEntry[] = [
                          {
                            id: "groupEditSuccess",
                            msg: intl.formatMessage(
                              {
                                defaultMessage: "Group {name} updated.",
                                description:
                                  "success notification for updating group. 'name' = name of the group",
                              },
                              {
                                name: "<strong>" + ng.name + "</strong>",
                              }
                            ),
                            autoclose: true,
                            type: "success",
                          },
                        ];
                        if (onSetGroupEntitlements && hasEntitlementChanges) {
                          onSetGroupEntitlements(
                            organizationId as string,
                            res.group.id as string,
                            groupEntitlements
                              ? groupEntitlements.map((v) => v.id as string)
                              : []
                          ).then((rres) => {
                            if (!isAddErrorAction(rres)) {
                              feedback.push({
                                id: "edit_organization_group_entitlements",
                                msg: intl.formatMessage(
                                  {
                                    defaultMessage:
                                      "Entitlements added for {name}.",
                                    description:
                                      "success notification for updating group entitlements. 'name' = name of the group",
                                  },
                                  {
                                    name:
                                      "<strong>" + res.group.name + "</strong>",
                                  }
                                ),
                                autoclose: true,
                                type: "success",
                              });
                            } else {
                              onClearError(rres.error?.errorId);
                              feedback.push({
                                id: "edit_organization_group_entitlements",
                                msg: intl.formatMessage(
                                  {
                                    defaultMessage:
                                      "Adding Entitlements for {name} failed.",
                                    description:
                                      "failure notification for updating group entitlements. 'name' = name of the group",
                                  },
                                  {
                                    name:
                                      "<strong>" + res.group.name + "</strong>",
                                  }
                                ),
                                type: "danger",
                              });
                            }
                            onShowFeedback(feedback);
                            onClose();
                          });
                        } else {
                          onShowFeedback(feedback);
                          onClose();
                        }
                      } else {
                        onClearError(res.error?.errorId);
                        onShowFeedback({
                          id:
                            "groupEditFailure_" +
                            (group ? (group.id as string) : "not_possible"),
                          msg: intl.formatMessage(
                            {
                              defaultMessage: "Updating group {name} failed.",
                              description:
                                "failure notification for updating group. 'name' = name of the group",
                            },
                            {
                              name:
                                "<strong>" +
                                (!!group ? group.name : ng.name) +
                                "</strong>",
                            }
                          ),
                          type: "danger",
                        });
                        onClose();
                      }
                    });
                  }
                  onClose();
                })();
              }
          : onClose
      }
      secondaryButton={
        group && allowEdit
          ? !edit
            ? {
                label: intl.formatMessage({
                  defaultMessage: "Close",
                  description: "close button label",
                }),
              }
            : {
                label: intl.formatMessage({
                  defaultMessage: "Cancel",
                  description: "secondary button label",
                }),
              }
          : undefined
      }
      onSecondaryAction={
        group && allowEdit
          ? !edit
            ? onClose
            : () => {
                history.push(
                  "/groups/" +
                    (group ? (group.id as string) : "not_possible") +
                    "/show"
                );
              }
          : undefined
      }
    >
      {(!isReady || group) && !edit && (
        <>
          <FieldList
            asOneLiners={true}
            fields={{
              name: {
                label: intl.formatMessage(OrganizationGroupLabels.name),
                value: group ? group.name : "",
              },
              type: {
                label: intl.formatMessage(OrganizationGroupLabels.type),
                value: group ? (
                  <GroupTypeBadge type={group.type} withLabel={true} />
                ) : (
                  ""
                ),
              },
              description: {
                label: intl.formatMessage(OrganizationGroupLabels.description),
                value: group ? group.description : "",
              },
              entitlements: {
                label: (
                  <FormattedMessage
                    defaultMessage="Entitlements"
                    description={"field label"}
                  />
                ),
                value: (
                  <>
                    {groupEntitlements &&
                      groupEntitlements.length > 0 &&
                      groupEntitlements.map((ent, i) => (
                        <>
                          <span key={"groupEntitlements_" + ent.id}>
                            {groupConf.showEntitlement && (
                              <NavigateAfterAction
                                href={
                                  "/groups/" +
                                  GroupModalKeys.showEntitlement +
                                  "/" +
                                  ent.id
                                }
                                action={onClose}
                              >
                                {ent.name}
                              </NavigateAfterAction>
                            )}
                            {!groupConf.showEntitlement && ent.name}
                          </span>
                          {groupEntitlements && i < groupEntitlements.length - 1
                            ? ", "
                            : undefined}
                        </>
                      ))}
                    {!groupEntitlements ||
                      (groupEntitlements.length === 0 && (
                        <FormattedMessage
                          defaultMessage="No assigned entitlements"
                          description={"display value for no entitlements"}
                        />
                      ))}
                  </>
                ),
              },
            }}
          />
        </>
      )}
      {(isReady || group) && edit && (
        <Form noValidate data-test-edit>
          <FormInput
            data-test-view-group-modal-group-name
            label={intl.formatMessage(OrganizationGroupLabels.name)}
            field="name"
            register={register}
            registerOptions={{
              required: true,
            }}
            hasValue={!!formValues["name"]}
            resolveValidity={validityResolver(formState)}
          >
            {errors &&
              errors.name &&
              errors.name.type &&
              errors.name.type === "required" && (
                <Form.Control.Feedback type="invalid">
                  <FontAwesomeIcon
                    icon={"exclamation-circle"}
                    className={"icon mr-1"}
                  />
                  <span className={"copy"}>
                    {intl.formatMessage(
                      {
                        defaultMessage: "{name} is required.",
                        description:
                          "Field validation error. 'name' = Field label for organization group name (OrganizationGroup.name)",
                      },
                      {
                        name: intl.formatMessage(OrganizationGroupLabels.name),
                      }
                    )}
                  </span>
                </Form.Control.Feedback>
              )}
          </FormInput>
          <FormInput
            data-test-view-group-modal-group-type
            label={intl.formatMessage(OrganizationGroupLabels.type)}
            field="type"
            register={register}
            registerOptions={
              !isEmployeesGroup
                ? {
                    required: false,
                    pattern: {
                      value: /^(?!employees$)/,
                      message: intl.formatMessage(
                        {
                          defaultMessage:
                            'Your organization can have only one group with {type} "employees". Please enter a different {type}.',
                          description:
                            "Field validation error. 'type' = Field label for organization group type (OrganizationGroup.type)",
                        },
                        {
                          type: intl.formatMessage(
                            OrganizationGroupLabels.type
                          ),
                        }
                      ),
                    },
                  }
                : {}
            }
            hasValue={!!formValues["type"]}
            readOnly={isEmployeesGroup}
            resolveValidity={validityResolver(formState)}
          >
            {isEmployeesGroup && (
              <Form.Text className={"info-feedback"}>
                <FontAwesomeIcon icon={"info-circle"} className={"icon mr-1"} />
                <FormattedMessage
                  defaultMessage='The type of "employees" group cannot be edited.'
                  description={
                    "field info for disabled group type edit when the type is employees"
                  }
                />
              </Form.Text>
            )}
            {errors &&
              errors.type &&
              errors.type.type &&
              errors.type.type === "required" && (
                <Form.Control.Feedback type="invalid">
                  <FontAwesomeIcon
                    icon={"exclamation-circle"}
                    className={"icon mr-1"}
                  />
                  {intl.formatMessage(
                    {
                      defaultMessage: "{type} is required.",
                      description:
                        "Field validation error'. type' = Field label for organization group type (OrganizationGroup.type)",
                    },
                    {
                      type: intl.formatMessage(OrganizationGroupLabels.type),
                    }
                  )}
                </Form.Control.Feedback>
              )}
            {errors &&
              errors.type &&
              errors.type.type &&
              errors.type.type === "pattern" && (
                <Form.Control.Feedback
                  type="invalid"
                  data-test-group-type-invalid
                >
                  <FontAwesomeIcon
                    icon={"exclamation-circle"}
                    className={"icon mr-1"}
                  />
                  {errors.type.message}
                </Form.Control.Feedback>
              )}
          </FormInput>
          <FormInput
            data-test-view-group-modal-group-description
            label={
              <FormattedMessage
                defaultMessage="Description"
                description={"field label"}
              />
            }
            field="description"
            type={"textarea"}
            register={register}
            registerOptions={{
              required: false,
            }}
            hasValue={!!formValues["description"]}
            resolveValidity={validityResolver(formState)}
          >
            {errors &&
              errors.description &&
              errors.description.type &&
              errors.description.type === "required" && (
                <Form.Control.Feedback type="invalid">
                  <FontAwesomeIcon
                    icon={"exclamation-circle"}
                    className={"icon mr-1"}
                  />
                  {intl.formatMessage(
                    {
                      defaultMessage: "{description} is required.",
                      description:
                        "Field validation error. 'description' = Field label for organization group description (OrganizationGroup.description)",
                    },
                    {
                      type: intl.formatMessage(
                        OrganizationGroupLabels.description
                      ),
                    }
                  )}
                </Form.Control.Feedback>
              )}
          </FormInput>
          {entitlements && (
            <Form.Group>
              <Table<Entitlement>
                disableLoadingIndicator={!isReady}
                maxRows={5}
                compact={true}
                data-test-select-entitlements
                header={
                  <Form.Label className={"pt-1 mb-0 mr-auto"}>
                    <FormattedMessage
                      defaultMessage="Entitlements"
                      description={"field label"}
                    />
                  </Form.Label>
                }
                pagination={false}
                data={entitlements}
                identifyingColumn={"id"}
                search={entitlements.length > TABLE_SEARCH_THRESHOLD}
                activeSearch={activeSearch}
                onSearch={onSetActiveSearch}
                columnToggle={false}
                reset={false}
                selection={{
                  multi: true,
                  selectAll: true,
                }}
                onSelectionChanged={onSetSelected}
                selected={selected}
                columns={[
                  {
                    key: "id",
                    label: intl.formatMessage(OrganizationGroupLabels.id),
                    isTechnical: true,
                    hidden: true,
                  },
                  {
                    key: "name",
                    label: intl.formatMessage({
                      defaultMessage: "Entitlement",
                      description:
                        "Column heading for the entitlements to select",
                    }),
                    sortable: true,
                    renderer: (props: {
                      cell: any;
                      row: any;
                      rowIndex: Number;
                      rendererData: any;
                    }) => {
                      return (
                        <>
                          {groupConf.showEntitlement && (
                            <NavigateAfterAction
                              href={
                                "/groups/" +
                                GroupModalKeys.showEntitlement +
                                "/" +
                                props.row.id
                              }
                              action={onClose}
                            >
                              {props.row.name}
                            </NavigateAfterAction>
                          )}
                          {!groupConf.showEntitlement && props.row.name}
                        </>
                      );
                    },
                  },
                ]}
              />
            </Form.Group>
          )}
        </Form>
      )}
      {!group && isReady && (
        <Feedback type={"danger"} show={true} asChild={true}>
          <p>
            <FormattedMessage
              defaultMessage="Something went wrong and the group could not be loaded. The group have been removed or you don't have sufficient access rights."
              description="message to be shown when there is no group to display"
            />
          </p>
        </Feedback>
      )}
    </Modal>
  );
}

export default ViewGroupModal;